import React, { useState, useEffect, useMemo } from 'react';
import type { ColDef } from '@ag-grid-community/core';
import { Loader2, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { utils, writeFile } from 'xlsx';
import AGDataGrid from '@/components/ag-data-grid';
import { useBreadcrumb } from '@/components/breadcrumb-provider';
import Container from '@/components/container';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import type { FdicLicenseRow, FdicLicensesResponse } from '@/hooks/use-reports';
import { useFdicLicensesReport } from '@/hooks/use-reports';

const REPORT_PATH = '/app/reports/fdic-licenses';

// Helper to format headers for display
const formatHeader = (header: string) => header.replaceAll('_', ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase());

// Column Definitions for FDIC Licenses
function fdicLicensesColDefs(): ColDef<FdicLicenseRow>[] {
  return [
    { field: 'email', headerName: 'Email', filter: true, sortable: true, resizable: true, width: 250 },
    { field: 'organization_legal_name', headerName: 'Organization', filter: true, sortable: true, resizable: true, width: 300 },
    { field: 'country', headerName: 'Country', filter: true, sortable: true, resizable: true, width: 100 },
    { field: 'state_province', headerName: 'State/Province', filter: true, sortable: true, resizable: true, width: 120 },
    { field: 'fdic_license', headerName: 'FDIC License', filter: true, sortable: true, resizable: true, width: 150 },
    { field: 'use_restriction', headerName: 'Use Restriction', filter: true, sortable: true, resizable: true, width: 200 },
    { field: 'use_restriction_note', headerName: 'Use Restriction Note', filter: true, sortable: true, resizable: true, width: 200 },
  ];
}

// Define headers for Excel export
const fdicLicensesHeadersExcel: (keyof FdicLicenseRow)[] = [
  'email',
  'organization_legal_name',
  'country',
  'state_province',
  'fdic_license',
  'use_restriction',
  'use_restriction_note',
];

// Pre-process data for Excel
function processFdicLicensesDataForExcel(row: FdicLicenseRow) {
  const processed: Record<string, string | number> = {};

  for (const header of fdicLicensesHeadersExcel) {
    const value = row[header];
    processed[header] = value ?? '';
  }

  return processed;
}

// Get Max Length for Column Width Calculation
function getMaxLength(arr: any[], key: string) {
  return Math.max(0, ...arr.map((item) => (item[key]?.toString() ?? '').length));
}

export default function FdicLicensesReport() {
  const { setLabel, removeLabel } = useBreadcrumb();
  const { mutateAsync, isPending, reset } = useFdicLicensesReport();
  const [reportData, setReportData] = useState<FdicLicensesResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Memoize column definitions
  const contactColumns = useMemo(() => fdicLicensesColDefs(), []);

  useEffect(() => {
    setLabel(REPORT_PATH, {
      name: 'FDIC Licenses Report',
      path: REPORT_PATH,
    });
    return () => removeLabel(REPORT_PATH);
  }, [setLabel, removeLabel]);

  // Generate Report function
  const handleGenerateReport = async (e: React.FormEvent) => {
    e.preventDefault();
    reset();
    setReportData(null);

    const generationPromise = mutateAsync({});

    toast.promise(generationPromise, {
      loading: 'Generating FDIC licenses report...',
      success: (responseData) => {
        setReportData(responseData);
        return 'Report preview generated successfully!';
      },
      error: (err) => {
        return err instanceof Error ? err.message : 'Failed to generate report.';
      },
    });
  };

  // Download Excel function
  const handleDownloadExcel = () => {
    if (!reportData) {
      toast.error('No report data available to download.');
      return;
    }

    try {
      // Process data for Excel
      const processedLicenses = reportData.licenses.map((license) => processFdicLicensesDataForExcel(license));

      // Create worksheet headers
      const excelHeaders = fdicLicensesHeadersExcel.map((header) => formatHeader(header));

      const ws = utils.json_to_sheet(processedLicenses, {
        header: fdicLicensesHeadersExcel,
      });

      // Add formatted headers
      utils.sheet_add_aoa(ws, [excelHeaders], { origin: 'A1' });

      // Auto-size columns with cap for long note column
      const colWidths = excelHeaders.map((header, index) => {
        const key = fdicLicensesHeadersExcel[index];
        const computed = Math.max(header.length, getMaxLength(processedLicenses, key)) + 2;
        const capped = key === 'use_restriction_note' ? Math.min(computed, 150) : computed;
        return { wch: capped };
      });
      ws['!cols'] = colWidths;

      // Create workbook and trigger download
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, 'FDIC Licenses');
      writeFile(wb, 'fdic_licenses_report.xlsx');

      toast.success('Excel report download started!');
    } catch (excelError) {
      console.error('Error generating Excel file:', excelError);
      toast.error('Failed to create Excel file.');
    }
  };

  return (
    <Container maxWidth={false} className="pb-12">
      <div className="mb-6">
        <h1 className="text-xl font-bold">FDIC Licenses Report</h1>
        <p className="text-sm text-muted-foreground">
          Generate a report of all registrants and their unique licenses, with focus on FDIC licenses for .bank domains.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Generate Report</CardTitle>
          <CardDescription>
            This report will show all contacts with licenses for .bank domains that have recent RDDS data (within 2 days).
            The report includes both FDIC-specific licenses and all other licenses for comprehensive license tracking.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleGenerateReport}>
          <CardFooter className="flex justify-end">
            <Button type="submit" disabled={isPending}>
              {isPending
                ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      Generating...
                    </>
                  )
                : (
                    'Generate Report'
                  )}
            </Button>
          </CardFooter>
        </form>
      </Card>

      {reportData && (
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Report Preview</CardTitle>
                <CardDescription>
                  Found
                  {' '}
                  {reportData.total_licenses}
                  {' '}
                  FDIC licenses
                </CardDescription>
              </div>
              <div className="flex items-center gap-4">
                <Input
                  type="search"
                  placeholder="Search preview data..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-64"
                />
                <Button onClick={handleDownloadExcel} variant="outline" size="sm" disabled={!reportData || isPending}>
                  <FileSpreadsheet className="mr-2 size-4" />
                  Save Report
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <AGDataGrid
              domLayout="autoHeight"
              rowData={reportData.licenses}
              columnDefs={contactColumns}
              quickFilterText={searchQuery}
              overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No FDIC licenses found</span>'
            />
          </CardContent>
        </Card>
      )}
    </Container>
  );
}
